// The payment stage of a fully signed deal, shown above the signed agreement on /Contract/:id —
// where both parties already land after signing, and where the Swish app returns the buyer.
// Server state is the only truth: it is read on load, while the page is visible (polled, so the
// two parties see each other's statements), and whenever the page comes back to the front —
// e.g. returning from the Swish app.
import React, { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { paymentApi } from "./paymentApi";
import { clearSwishReturn, readSwishReach, readSwishReturn, subscribeSwishReach } from "./swishApp2App";
import { DealIdentityBand, StageLabel, StagePlaceholder } from "./PaymentParts";
import SellerFlowTrack from "../SellerFlowTrack";
import BuyerPaymentPanel from "./BuyerPaymentPanel";
import SellerPaymentPanel from "./SellerPaymentPanel";

export const PAYMENT_POLL_MS = 5000;

// The deal's progress rail, carried on from the signing flow: payment is the step after the
// contract, and it is the one in progress whenever this stage is on screen. Same component and
// the same milestone keys the seller and buyer journeys already use, so the rail reads as one
// continuous track rather than a new thing on this page.
const PAYMENT_TRACK = [
  { key: "vehicle", atStep: 0 },
  { key: "registration", atStep: 1 },
  { key: "connection", atStep: 2 },
  { key: "contract", atStep: 3 },
  { key: "payment", atStep: 4 },
  // A confirmed payment leads to Ägarbyte, which has a stage of its own for BOTH sides — the
  // seller reports the transfer, the buyer waits for them — so the rail says so while the payment
  // is still the current step.
  { key: "ownership", atStep: 5 },
];
const PAYMENT_TRACK_STEP = 4;
const OWNERSHIP_TRACK_STEP = 5;

export default function DealPaymentStage({ contractId, onAdvance }) {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const [state, setState] = useState(null);
  // Read the Swish return once and take it out of the address bar straight away.
  const [swishReturn] = useState(() => {
    const ret = readSwishReturn();
    if (ret) clearSwishReturn();
    return ret;
  });
  const reach = useSyncExternalStore(subscribeSwishReach, readSwishReach, () => "app");

  const refresh = useCallback(async () => {
    try {
      const next = await paymentApi.getState(contractId);
      setState((prev) => (prev && next && prev.revision === next.revision && prev.stage === next.stage ? prev : next));
    } catch (_) {
      /* not a party / not reachable: the stage simply does not show */
    }
  }, [contractId]);

  useEffect(() => {
    if (!contractId) return undefined;
    refresh();
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [contractId, refresh]);

  // The seller's confirmation ARRIVING while this page waits for it is a different event from a
  // payment found already confirmed, and the two lead to different places: the first hands the
  // buyer on to Agarbyte by itself and completes Betalning on the rail as it goes, the second
  // leaves Agarbyte standing as the current step with a control to go there. Read here, off the
  // stages themselves, because this is where server state changes.
  const [confirmedLive, setConfirmedLive] = useState(false);
  const lastStageRef = useRef(null);
  useEffect(() => {
    if (lastStageRef.current === "reported" && state?.stage === "confirmed") setConfirmedLive(true);
    if (state) lastStageRef.current = state.stage;
  }, [state]);

  const settled = state?.stage === "confirmed" || state?.stage === "notOpen";
  useEffect(() => {
    if (!contractId || !state || settled) return undefined;
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") refresh();
    }, PAYMENT_POLL_MS);
    return () => window.clearInterval(timer);
  }, [contractId, state, settled, refresh]);

  // Until the stage knows where the deal stands it shows the shape of what is coming, as the
  // reference does - never a blank frame. A payment that is not open belongs to the page's own
  // guard, which moves the user on.
  if (!state || state.stage === "notOpen") return <StagePlaceholder label={t("payment.preparing")} />;

  // A party who FINDS the payment confirmed is between the two stages: Betalning is done and
  // Ägarbyte is where they are going, so the rail says Ägarbyte. A confirmation that arrives or is
  // made live does not, because the handoff completes Betalning on the way out — one path to the
  // rail, never two.
  const railStep = state.stage === "confirmed" && !confirmedLive ? OWNERSHIP_TRACK_STEP : PAYMENT_TRACK_STEP;

  return (
    <>
      {/* The rail sits at page level, exactly as it does on the signing pages: flow-shell-track
          gives it its own 720px measure, so it is not squeezed into the stage's 42rem body. */}
      <div className="flow-shell-track">
        <SellerFlowTrack
          activeStep={railStep}
          t={t}
          milestones={PAYMENT_TRACK}
          ns={state.role === "buyer" ? "buyer" : "seller"}
        />
      </div>
      <section
        className="mx-auto w-full pb-8"
        style={{ maxWidth: "42rem" }}
        aria-labelledby="deal-payment-heading"
        data-testid="deal-payment-stage"
      >
      <span id="deal-payment-heading" className="sr-only">{t("payment.stage")}</span>
      <DealIdentityBand plate={state.registrationNo} amount={state.amount} language={language} t={t} />
      <StageLabel confirmed={state.stage === "confirmed"} t={t} />
      {state.role === "buyer" ? (
        <BuyerPaymentPanel state={state} onState={setState} onRefresh={refresh} t={t} language={language} reach={reach} swishReturn={swishReturn} onAdvance={onAdvance} confirmedLive={confirmedLive} />
      ) : (
        <SellerPaymentPanel state={state} onState={setState} onRefresh={refresh} t={t} language={language} onAdvance={onAdvance} confirmedLive={confirmedLive} />
      )}
      </section>
    </>
  );
}
